import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url, chatbotUrl } from '../../util/config';

// ── Test Panel ────────────────────────────────────────────────────────────────
function TestPanel({ agent, onClose, token }) {
    const [messages,  setMessages]  = useState([]);
    const [input,     setInput]     = useState('');
    const [sending,   setSending]   = useState(false);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const sendMessage = async (text) => {
        const msg = (text || input).trim();
        if (!msg || sending) return;
        setInput('');
        setSending(true);
        setMessages(p => [...p, { role: 'user', text: msg, time: Date.now() }]);
        // thinking bubble
        const thinkId = Date.now();
        setMessages(p => [...p, { role: 'thinking', id: thinkId }]);

        try {
            const res  = await fetch(`${chatbotUrl}/test/simulate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    from:            '919876543210',
                    text:            msg,
                    phone_number_id: agent.phone_number_id,
                }),
            });
            const data = await res.json();
            setMessages(p => p.filter(m => m.id !== thinkId));
            if (data.success && data.data?.response) {
                setMessages(p => [...p, { role: 'bot', text: data.data.response, mode: data.data.mode, source: data.data.source, time: Date.now() }]);
            }
        } catch (e) {
            setMessages(p => p.filter(m => m.id !== thinkId));
            setMessages(p => [...p, { role: 'system', text: 'Error connecting to chatbot' }]);
        }
        setSending(false);
    };

    const resetSession = async () => {
        setMessages([]);
        try {
            await fetch(`${chatbotUrl}/test/reset-session?phone=919876543210`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            toast.success('Session reset', { autoClose: 1000 });
        } catch (e) { console.warn('resetSession failed', e); }
    };

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 380, height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ background: '#075e54', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{agent.avatar || '🤖'}</span>
                <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{agent.name}</p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', margin: 0 }}>Test Mode</p>
                </div>
                <button onClick={resetSession} title="Reset session"
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', fontSize: 12 }}>
                    <i className="bi bi-arrow-clockwise"></i>
                </button>
                <button onClick={onClose}
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', fontSize: 14 }}>
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#ece5dd', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
                        <p style={{ fontSize: 13, margin: 0, fontWeight: 600 }}>Send a message to test your agent</p>
                    </div>
                )}
                {messages.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                        {msg.role === 'thinking' ? (
                            <div style={{ background: '#fff', padding: '10px 14px', borderRadius: '4px 16px 16px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    {[0,1,2].map(j => <div key={j} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: `bounce 1.2s ${j*0.2}s infinite` }}/>)}
                                </div>
                            </div>
                        ) : msg.role === 'system' ? (
                            <div style={{ width: '100%', textAlign: 'center' }}>
                                <span style={{ fontSize: 11, color: '#6b7280', background: 'rgba(0,0,0,0.06)', padding: '3px 12px', borderRadius: 20 }}>{msg.text}</span>
                            </div>
                        ) : (
                            <div style={{ maxWidth: '80%' }}>
                                <div style={{ padding: '8px 12px', borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: msg.role === 'user' ? '#dcf8c6' : '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', fontSize: 12, color: '#111827', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                    {msg.text}
                                </div>
                                {msg.role === 'bot' && msg.source && (
                                    <span style={{ fontSize: 10, color: '#9ca3af', display: 'block', marginTop: 2, paddingLeft: 4 }}>{msg.source}</span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                <div ref={endRef}/>
            </div>

            {/* Quick messages */}
            <div style={{ padding: '8px 12px 4px', background: '#f0f0f0', display: 'flex', gap: 6, overflowX: 'auto' }}>
                {['Hello', 'Hi', 'Help'].map(q => (
                    <button key={q} onClick={() => sendMessage(q)} disabled={sending}
                        style={{ flexShrink: 0, padding: '4px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', fontSize: 11, color: '#374151' }}>
                        {q}
                    </button>
                ))}
            </div>

            {/* Input */}
            <div style={{ background: '#f0f0f0', padding: '8px 12px 12px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, background: '#fff', borderRadius: 20, padding: '8px 14px' }}>
                    <textarea value={input} onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
                        placeholder="Type a message..." rows={1}
                        style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', fontSize: 13, background: 'transparent', fontFamily: 'inherit', maxHeight: 80, lineHeight: 1.4 }}/>
                </div>
                <button onClick={() => sendMessage()} disabled={!input.trim() || sending}
                    style={{ width: 40, height: 40, borderRadius: '50%', background: !input.trim() || sending ? '#d1d5db' : '#25d366', border: 'none', cursor: !input.trim() || sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="bi bi-send-fill" style={{ fontSize: 14, color: '#fff' }}></i>
                </button>
            </div>
            <style>{`@keyframes bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-4px)}}`}</style>
        </div>
    );
}

const MODE_META = {
    simple:     { label: 'Simple Agent',     color: '#16a34a', bg: '#f0fdf4', icon: 'bi-lightning-charge' },
    structured: { label: 'Structured Agent', color: '#d97706', bg: '#fffbeb', icon: 'bi-layers'           },
    flow:       { label: 'Flow Mode',        color: '#1565c0', bg: '#eff6ff', icon: 'bi-diagram-3'        },
};

function AgentCard({ agent, onEdit, onDelete, onPublish, onUnpublish, onTest, onDuplicate }) {
    const [menuOpen,    setMenuOpen]    = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'unpublish' | 'delete' | null
    const [acting,      setActing]      = useState(false);

    const mode   = MODE_META[agent.mode] || MODE_META.simple;
    const isLive = agent.is_published;

    const handleConfirm = async () => {
        setActing(true);
        try {
            if (confirmAction === 'unpublish') await onUnpublish(agent);
            if (confirmAction === 'delete')    await onDelete(agent);
        } finally {
            setActing(false);
            setConfirmAction(null);
        }
    };

    return (
        <div style={{ background: '#fff', border: `1px solid ${confirmAction ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 16, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.2s', display: 'flex', flexDirection: 'column' }}>

            {/* ── Inline confirmation banner ─────────────────────────────── */}
            {confirmAction && (
                <div style={{ padding: '14px 20px', background: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                            {confirmAction === 'unpublish'
                                ? `Take "${agent.name}" offline?`
                                : `Delete "${agent.name}" permanently?`}
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>
                            {confirmAction === 'unpublish'
                                ? `This agent will stop responding on ${agent.whatsapp_no || agent.phone_number_id}. You can republish anytime.`
                                : 'This action cannot be undone. All agent configuration will be lost.'}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button onClick={() => setConfirmAction(null)}
                            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer' }}>
                            Cancel
                        </button>
                        <button onClick={handleConfirm} disabled={acting}
                            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, border: 'none', borderRadius: 8, background: '#dc2626', color: '#fff', cursor: acting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: acting ? 0.7 : 1 }}>
                            {acting
                                ? <><div className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2, color: '#fff' }}></div> Processing...</>
                                : confirmAction === 'unpublish' ? 'Take Offline' : 'Delete'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Card body ─────────────────────────────────────────────── */}
            <div style={{ padding: '20px 20px 16px', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                    {/* Avatar + name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, background: mode.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                            {agent.avatar || '🤖'}
                        </div>
                        <div>
                            <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>{agent.name}</p>
                            <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>{agent.description || 'No description'}</p>
                        </div>
                    </div>

                    {/* 3-dot menu */}
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setMenuOpen(p => !p)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', color: '#9ca3af', borderRadius: 8, fontSize: 16 }}>
                            <i className="bi bi-three-dots-vertical"></i>
                        </button>
                        {menuOpen && (
                            <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 170, overflow: 'hidden' }}
                                onMouseLeave={() => setMenuOpen(false)}>
                                <button onClick={() => { onEdit(agent); setMenuOpen(false); }}
                                    style={menuItemStyle('#374151')}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <i className="bi bi-pencil" style={{ fontSize: 13 }}></i> Edit Agent
                                </button>

                                <button onClick={() => { onTest(agent); setMenuOpen(false); }}
                                    style={menuItemStyle('#7c3aed')}
                                    onMouseEnter={e => e.currentTarget.style.background = '#faf5ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <i className="bi bi-chat-dots" style={{ fontSize: 13 }}></i> Test Agent
                                </button>

                                <button onClick={() => { onDuplicate(agent); setMenuOpen(false); }}
                                    style={menuItemStyle('#374151')}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <i className="bi bi-copy" style={{ fontSize: 13 }}></i> Duplicate
                                </button>

                                {isLive ? (
                                    <button onClick={() => { setConfirmAction('unpublish'); setMenuOpen(false); }}
                                        style={menuItemStyle('#d97706')}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <i className="bi bi-pause-circle" style={{ fontSize: 13 }}></i> Take Offline
                                    </button>
                                ) : (
                                    <button onClick={() => { onPublish(agent); setMenuOpen(false); }}
                                        style={menuItemStyle('#16a34a')}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <i className="bi bi-rocket" style={{ fontSize: 13 }}></i>
                                        {agent.phone_number_id ? 'Go Live Again' : 'Publish'}
                                    </button>
                                )}

                                <div style={{ height: 1, background: '#f3f4f6', margin: '4px 0' }}/>

                                <button onClick={() => { setConfirmAction('delete'); setMenuOpen(false); }}
                                    style={menuItemStyle('#dc2626')}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <i className="bi bi-trash" style={{ fontSize: 13 }}></i> Delete Agent
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mode + Status */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: mode.bg, color: mode.color }}>
                        <i className={`bi ${mode.icon}`} style={{ fontSize: 11 }}></i> {mode.label}
                    </span>
                    {isLive ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block', animation: 'pulse 2s infinite' }}></span>
                            Live
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: '#f9fafb', color: '#6b7280' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d1d5db', display: 'inline-block' }}></span>
                            Draft
                        </span>
                    )}
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bi bi-tag" style={{ fontSize: 10 }}></i> {agent.rag_tags?.length || 0} KB tags
                    </span>
                    <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bi bi-shield" style={{ fontSize: 10 }}></i> {agent.guardrails?.length || 0} guardrails
                    </span>
                    <span style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bi bi-chat-dots" style={{ fontSize: 10 }}></i> {agent.faqs?.length || 0} FAQs
                    </span>
                    {isLive && agent.whatsapp_no && (
                        <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-whatsapp" style={{ fontSize: 10 }}></i> {agent.whatsapp_no}
                        </span>
                    )}
                    {!isLive && agent.whatsapp_no && (
                        <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="bi bi-whatsapp" style={{ fontSize: 10 }}></i> {agent.whatsapp_no} (offline)
                        </span>
                    )}
                </div>
            </div>

            {/* ── Card footer ───────────────────────────────────────────── */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {new Date(agent.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <button onClick={() => onEdit(agent)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#eff6ff', color: '#1565c0', border: '1px solid #bfdbfe', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    <i className="bi bi-pencil" style={{ fontSize: 11 }}></i> Edit
                </button>
            </div>
        </div>
    );
}

const menuItemStyle = (color) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', width: '100%', border: 'none',
    background: 'transparent', cursor: 'pointer',
    fontSize: 13, color, textAlign: 'left', transition: 'background 0.1s',
});

export default function Agents() {
    const navigate = useNavigate();
    const [agents,     setAgents]     = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [filter,     setFilter]     = useState('all');
    const [search,     setSearch]     = useState('');
    const [testAgent,  setTestAgent]  = useState(null);
    const token = localStorage.getItem('token');

    const fetchAgents = async () => {
        setLoading(true);
        try {
            const res  = await fetch(`${url}/agents`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setAgents(data.data);
        } catch (e) { toast.error('Failed to load agents'); }
        setLoading(false);
    };

    useEffect(() => { fetchAgents(); }, []);

    const handleDelete = async (agent) => {
        try {
            const res  = await fetch(`${url}/agents/${agent._id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) { toast.success('Agent deleted'); fetchAgents(); }
            else toast.error(data.error);
        } catch (e) { toast.error('Delete failed'); }
    };

    const handleUnpublish = async (agent) => {
        try {
            const res  = await fetch(`${url}/agents/${agent._id}/unpublish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) { toast.success(`"${agent.name}" is now offline`); fetchAgents(); }
            else toast.error(data.error);
        } catch (e) { toast.error('Failed'); }
    };

    const handlePublish = async (agent) => {
        if (agent.phone_number_id) {
            // Has stored number — one click republish
            try {
                const res  = await fetch(`${url}/agents/${agent._id}/publish`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ phone_number_id: agent.phone_number_id, whatsapp_no: agent.whatsapp_no }),
                })
                const data = await res.json()
                if (data.success) { toast.success(`"${agent.name}" is now live! 🚀`); fetchAgents(); }
                else toast.error(data.error)
            } catch (e) { toast.error('Failed to publish') }
        } else {
            // No stored number — go to deploy step
            navigate(`/agents/create?id=${agent._id}&step=deploy`)
        }
    };
    const handleEdit      = (agent) => navigate(`/agents/create?id=${agent._id}`);
    const handleTest      = (agent) => setTestAgent(agent);
    const handleDuplicate = async (agent) => {
        try {
            const { _id, createdAt, updatedAt, __v, is_published, phone_number_id, whatsapp_no, ...rest } = agent;
            const res  = await fetch(`${url}/agents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...rest, name: `${agent.name} (Copy)`, is_published: false, phone_number_id: '', whatsapp_no: '' }),
            });
            const data = await res.json();
            if (data.success) { toast.success('Agent duplicated successfully!'); fetchAgents(); }
            else toast.error(data.error);
        } catch (e) { toast.error('Duplicate failed'); }
    };

    const displayed = agents
        .filter(a => filter === 'all' ? true : filter === 'live' ? a.is_published : !a.is_published)
        .filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()));

    const liveCount  = agents.filter(a => a.is_published).length;
    const draftCount = agents.filter(a => !a.is_published).length;

    return (
        <WrapperComponent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Agents</h5>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                            Create and manage your WhatsApp AI agents. Each agent deployed on one WhatsApp number.
                        </p>
                    </div>
                    <button onClick={() => navigate('/agents/create')}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'linear-gradient(135deg, #1565c0, #1e40af)', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700, boxShadow: '0 2px 8px rgba(21,101,192,0.3)' }}>
                        <i className="bi bi-plus-lg"></i> Create Agent
                    </button>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 12 }}>
                    {[
                        { label: 'Total', value: agents.length,  color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe' },
                        { label: 'Live',  value: liveCount,      color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                        { label: 'Draft', value: draftCount,     color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
                    ].map(s => (
                        <div key={s.label} style={{ padding: '12px 20px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</span>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</span>
                        </div>
                    ))}
                </div>

                {/* Filter + Search */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                        {[['all', 'All'], ['live', '🟢 Live'], ['draft', '⚪ Draft']].map(([val, lbl]) => (
                            <button key={val} onClick={() => setFilter(val)}
                                style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 20, border: `1.5px solid ${filter === val ? '#1565c0' : '#e5e7eb'}`, background: filter === val ? '#eff6ff' : '#fff', color: filter === val ? '#1565c0' : '#374151', cursor: 'pointer' }}>
                                {lbl}
                            </button>
                        ))}
                    </div>
                    <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13 }}></i>
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agents..."
                            style={{ width: '100%', padding: '8px 12px 8px 34px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}/>
                    </div>
                </div>

                {/* Grid */}
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                    </div>
                ) : displayed.length === 0 ? (
                    <div style={{ background: '#fff', border: '2px dashed #e5e7eb', borderRadius: 20, padding: 80, textAlign: 'center' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
                        <p style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
                            {search || filter !== 'all' ? 'No agents match your filter' : 'No agents yet'}
                        </p>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 24px' }}>
                            {search || filter !== 'all' ? 'Try a different search or filter' : 'Create your first AI agent and deploy it on WhatsApp in minutes'}
                        </p>
                        {!search && filter === 'all' && (
                            <button onClick={() => navigate('/agents/create')}
                                style={{ padding: '12px 24px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                                <i className="bi bi-plus-lg" style={{ marginRight: 8 }}></i>Create Your First Agent
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
                        {displayed.map(agent => (
                            <AgentCard key={agent._id} agent={agent}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onPublish={handlePublish}
                                onUnpublish={handleUnpublish}
                                onTest={handleTest}
                                onDuplicate={handleDuplicate}
                                onRefresh={fetchAgents}
                            />
                        ))}
                    </div>
                )}
            </div>

            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            <ToastContainer/>

            {/* Test Agent Panel */}
            {testAgent && (
                <>
                    <div onClick={() => setTestAgent(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199 }}/>
                    <TestPanel agent={testAgent} token={token} onClose={() => setTestAgent(null)}/>
                </>
            )}
        </WrapperComponent>
    );
}
